import Modal from 'react-modal'
import React, { useMemo } from 'react'
import ItemModalQuestions from 'components/ItemModal/ItemModalQuestions'
import { locationQuestionsToQuestionsAdapter } from 'components/ItemModal/helpers'
import Image from 'components/Image'
import focusOnError from 'components/ui/focusOnError'
import { Form } from 'react-final-form'
import FormTextField from 'components/ui/FormTextField'
import { SubmitValues } from 'components/ItemModal/itemModalTypes'
import makeValidate from 'components/ItemModal/makeValidate'
import FormError from 'components/ui/FormError'
import getCartItemInput from 'components/ItemModal/getCartItemInput'
import BaseItemModalBottom from 'components/ItemModal/BaseItemModalBottom'
import Tag from 'components/Tag'
import { PlusIcon } from '@heroicons/react/24/solid'

import { LocationItemStatus } from 'global-graphql-types'

import { itemModalCustomStyles } from './styles'
import { LocationItemQuery } from './types/LocationItemQuery'
import { ItemQuestionsFragment } from './types/ItemQuestionsFragment'

type LocationItemType = NonNullable<LocationItemQuery['locationItem']>

type LocationItemQuestionsType =
  ItemQuestionsFragment['locationItemQuestions'][number]

function getButtonText(forUpdate: boolean, quantity: number | null) {
  if (forUpdate) {
    return quantity === 0 ? 'Remove' : 'Update'
  }
  return 'Add'
}

export const CloseModalIcon = ({ closeModal }: { closeModal: () => void }) => {
  return (
    <div className="fixed top-2 right-2 z-30">
      <div
        aria-label="Close button"
        className="bg-red bg-white opacity-70 rounded-full outline-none-mouse"
        onClick={closeModal}
        onKeyDown={(e) => {
          if (e.keyCode === 13) {
            closeModal()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <PlusIcon className="rotate-45 w-9 h-9 text-gray-500" />
      </div>
    </div>
  )
}

type Props = {
  modalIsOpen: boolean
  closeModal: () => void
  initialValues: SubmitValues
  onSubmit: (values: SubmitValues) => void
  locationItem: LocationItemType
  forUpdate?: boolean
  notice?: React.ReactElement
  canOrder?: boolean
}

const BaseItemModal = ({
  modalIsOpen,
  closeModal,
  onSubmit,
  initialValues,
  locationItem,
  forUpdate = false,
  notice,
  canOrder = true
}: Props) => {
  const questions = useMemo(
    () =>
      locationItem.locationItemQuestions.filter(
        (q: LocationItemQuestionsType) => q.status === LocationItemStatus.Active
      ),
    [locationItem.locationItemQuestions]
  )
  const { item } = locationItem

  const formQuestions = locationQuestionsToQuestionsAdapter(
    locationItem.locationItemQuestions
  )

  const validate = makeValidate(formQuestions)

  return (
    <Modal
      contentLabel="add item"
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={itemModalCustomStyles}
    >
      <div>
        <CloseModalIcon closeModal={closeModal} />

        <Form<SubmitValues>
          decorators={[focusOnError]}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validate={validate}
        >
          {({ handleSubmit, values, submitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="w-full shadow-lg box-border">
                <div className="flex justify-center relative">
                  <Image
                    alt={item.name}
                    className="rounded-t"
                    height={355}
                    src={item.image.high}
                    width={355}
                  />
                  <div className="absolute bottom-1 left-4 right-4 z-30 flex flex-wrap">
                    {item.tags.map((tag) => (
                      <Tag key={tag.id} tag={tag} />
                    ))}
                  </div>
                </div>
                {notice}

                <FormError className="mx-4 mt-4 mb-2" />
                <div className="p-4 space-y-4">
                  <div>{item.description}</div>

                  <ItemModalQuestions questions={formQuestions} />

                  {item.hasSpecialInstructions && (
                    <FormTextField
                      label="Special Instructions"
                      name="specialInstructions"
                    />
                  )}
                </div>
                <BaseItemModalBottom
                  buttonText={getButtonText(forUpdate, values.quantity)}
                  canOrder={canOrder}
                  forUpdate={forUpdate}
                  isDelete={forUpdate ? values.quantity === 0 : false}
                  item={item}
                  itemInput={getCartItemInput({
                    questions,
                    values,
                    id: locationItem.id,
                    omitInstruction: true
                  })}
                  submitting={submitting}
                />
              </div>
            </form>
          )}
        </Form>
      </div>
    </Modal>
  )
}

export default BaseItemModal
